import type { EnumDefinitionCreateOperation } from '@festina/configuration-general-types/configuration'
import { FormikForm } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import * as Yup from 'yup'

import { useOperationsContext } from '../../workspace/operationsContext'
import { DraftCreationFormId } from '../draftCreationModal/DraftCreationModal'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftCreationProps,
} from '../draftCreationModal/types'
import type { EnumDraftCreationData } from './helpers'
import { NonConfiguredEnumsCombobox } from './NonConfiguredEnumsCombobox'

export const EnumDraftCreationForm = ({
  layer,
  block,
  onStatusChanged,
}: DraftCreationProps<'enumDefinition'>) => {
  const translate = useTranslate()
  const initialValues: EnumDraftCreationData = {
    name: '',
  }
  const { onAddOrUpdateOperation, configuredEnums } = useOperationsContext()

  const handleSubmit = async (
    values: EnumDraftCreationData,
  ): Promise<DraftCreatedResult> => {
    const createOperation = {
      _type: 'EnumDefinitionCreateOperation',
      entity: {
        _type: 'EnumDefinitionInOut',
        name: values.name,
        ordinal: false,
        deprecated: false,
      },
    } satisfies EnumDefinitionCreateOperation

    const createdOnWorkspaceKey = await onAddOrUpdateOperation({
      operation: createOperation,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    return {
      createdDraft: {
        type: 'enumDefinition',
        name: values.name,
        layer,
        block,
      },
      addedToWorkspaceKey: createdOnWorkspaceKey,
    }
  }

  const validationSchema: Yup.ObjectSchema<EnumDraftCreationData> = Yup.object({
    name: Yup.string()
      .required(translate('validation.required'))
      .test(
        'noSpaces',
        translate('validation.noSpaces'),
        (value) => !/\s/.test(value),
      )
      .test('noDuplicates', translate('validation.unique'), (value) => {
        const currentBlockLayerEnums = configuredEnums
          ?.get(block)
          ?.get(layer)
          ?.get(value)
        const matchingEnum =
          !!currentBlockLayerEnums && !!currentBlockLayerEnums.internalOperation

        return value ? !matchingEnum : true
      }),
  })

  return (
    <FormikForm
      formId={DraftCreationFormId}
      initialValues={initialValues}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmit)}
      validationSchema={validationSchema}
      shouldSanitizeOnSubmit={false}
    >
      <NonConfiguredEnumsCombobox layer={layer} block={block} />
    </FormikForm>
  )
}
